import axios from "axios";

// En local (npm run serve), el front vive en :8080 y el back en :3000,
// así que hace falta la URL absoluta. En producción, VUE_APP_API_URL se
// setea a "/api" (ver .env.production) porque nginx sirve front y back
// bajo el mismo dominio y hace proxy de /api al backend — necesario
// además para que las cookies de sesión (SameSite=Strict) funcionen.
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || "http://localhost:3000/api",
  // El access/refresh token ahora viven en cookies httpOnly seteadas por el
  // backend; withCredentials es lo que hace que el navegador las adjunte
  // (y acepte las que llegan) en peticiones cross-origin.
  withCredentials: true,
});

// ─── Canal de comunicación entre pestañas ────────────────────────────────────
const channel = new BroadcastChannel("auth_channel");

// ─── Estado local de refresh ──────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
  failedQueue = [];
};

// ─── Lock atómico ─────────────────────────────────────────────────────────────
// Sigue siendo necesario aunque el token ya no viaje por JS: si dos pestañas
// disparan /refresh a la vez, la segunda puede pisar/invalidar la rotación
// de la primera en la BD. El lock solo coordina turnos, no guarda tokens.
const LOCK_KEY = "refresh_lock";
const LOCK_TTL = 8000;

function acquireLock() {
  const now = Date.now();
  const raw = localStorage.getItem(LOCK_KEY);

  if (raw) {
    const { ts } = JSON.parse(raw);
    if (now - ts < LOCK_TTL) return null;
  }

  const lockId = `${now}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(LOCK_KEY, JSON.stringify({ ts: now, id: lockId }));

  const written = JSON.parse(localStorage.getItem(LOCK_KEY));
  if (written?.id !== lockId) return null;

  return lockId;
}

function releaseLock(lockId) {
  const raw = localStorage.getItem(LOCK_KEY);
  if (!raw) return;
  const { id } = JSON.parse(raw);
  if (id === lockId) localStorage.removeItem(LOCK_KEY);
}

// ─── Esperar a que otra pestaña termine su refresh ────────────────────────────
function waitForRefreshFromLeader(timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      channel.removeEventListener("message", handler);
      reject(new Error("Timeout esperando refresh del líder"));
    }, timeoutMs);

    const handler = ({ data }) => {
      if (data.type === "TOKEN_REFRESHED") {
        clearTimeout(timer);
        channel.removeEventListener("message", handler);
        resolve();
      }
      if (data.type === "REFRESH_FAILED" || data.type === "SESSION_CLOSED") {
        clearTimeout(timer);
        channel.removeEventListener("message", handler);
        reject(new Error(data.type));
      }
    };

    channel.addEventListener("message", handler);
  });
}

// ─── Mandar al login ──────────────────────────────────────────────────────────
// Codes del backend que significan "la sesión ya no sirve": no tiene sentido
// intentar /refresh (el backend ya borró las cookies), se va directo al login.
// SESION_EXPIRADA llega en jobs cuya búsqueda ya no existe en el servidor.
export const CODIGOS_IR_A_LOGIN = ["SESSION_REVOKED", "AUTH_TOKEN_INVALID", "SESION_EXPIRADA"];

export function irAlLogin() {
  channel.postMessage({ type: "SESSION_CLOSED" });
  // Si ya estamos en /signin (típico: /me falla porque nunca hubo
  // sesión, no porque una activa haya muerto), NO recargar: asignar
  // location.href a la misma URL igual dispara una recarga completa
  // del navegador, que vuelve a montar la app, vuelve a llamar a /me,
  // vuelve a fallar el refresh y entra en loop infinito.
  if (window.location.pathname !== "/signin") {
    window.location.href = "/signin";
  }
}

// ─── Interceptor response ─────────────────────────────────────────────────────
// Ya no hace falta interceptor de request para adjuntar Authorization: el
// navegador manda la cookie accessToken solo. Tampoco hay refresh proactivo
// por temporizador: sin acceso a la cookie desde JS no se puede leer su
// expiración, así que el refresh es reactivo (se dispara ante un 401).
api.interceptors.response.use(
  (res) => res,

  async (error) => {
    const original = error.config;

    const skipRefresh =
      original?.url?.endsWith("/login") ||
      original?.url?.endsWith("/refresh");

    if (error.response?.status !== 401 || original._retry || skipRefresh) {
      return Promise.reject(error);
    }

    if (CODIGOS_IR_A_LOGIN.includes(error.response.data?.code)) {
      irAlLogin();
      return Promise.reject(error);
    }

    original._retry = true;

    // ¿Ya está refrescando ESTA pestaña? → encolar
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: () => resolve(api(original)),
          reject,
        });
      });
    }

    // Intentar adquirir el lock global
    const lockId = acquireLock();

    if (!lockId) {
      // Otra pestaña tiene el lock → esperar a que termine
      try {
        await waitForRefreshFromLeader();
        return api(original);
      } catch {
        return Promise.reject(new Error("No se pudo refrescar la sesión"));
      }
    }

    // Soy el líder: hago el refresh (refreshToken va en cookie, sin body)
    isRefreshing = true;

    try {
      await api.post("/refresh");

      channel.postMessage({ type: "TOKEN_REFRESHED" });
      processQueue(null);
      return api(original);

    } catch (err) {
      processQueue(err);
      channel.postMessage({ type: "REFRESH_FAILED" });

      // Sea 401/403 (refresh token inválido o expirado), 429 (rate limit
      // compartido por IP agotado) o cualquier otro fallo (500, red caída):
      // si /refresh no pudo completarse, el accessToken sigue muerto y no
      // hay forma de que la petición original prospere. Mandar al login
      // siempre evita que el usuario quede varado viendo un error genérico
      // sin saber que tiene que volver a iniciar sesión.
      irAlLogin();

      return Promise.reject(err);

    } finally {
      isRefreshing = false;
      releaseLock(lockId);
    }
  }
);

// ─── Cierre de sesión desde otra pestaña ─────────────────────────────────────
channel.addEventListener("message", ({ data }) => {
  if (data.type === "SESSION_CLOSED" && window.location.pathname !== "/signin") {
    window.location.href = "/signin";
  }
});

export default api;
